import { useState } from 'react';

import useMapContext from 'components/Map/hooks/useMapContext';

import { RESET_STATE } from './constants';
import type { DrawingMode, DrawingTool, PointMode, Props, ResetState } from './types';

const useBoundaryReset = ({ onCompleteDrawing, onDrawingReset, onExitDrawing }: Props) => {
    const { mapState } = useMapContext();
    const {
        isDrawing,
        selectedFeature,
        setDrawingMode,
        setDrawingTool,
        setPointMode,
        setSelectedFeature
    } = mapState;

    const [isResetPopupOpen, setIsResetPopupOpen] = useState(false);

    const [resetState, setResetState] = useState<ResetState>();

    const handleClose = () => {
        setResetState(undefined);
        setIsResetPopupOpen(false);
    };

    const shouldShowResetPopup = selectedFeature || isDrawing;

    const RESET_STATE_MAP: Record<RESET_STATE, ResetState> = {
        [RESET_STATE.DELETE_AND_START_OVER]: {
            onAction: () => {
                onDrawingReset?.();
                handleClose();
            },
            onClose: handleClose,
            resetType: RESET_STATE.DELETE_AND_START_OVER
        },
        [RESET_STATE.LEAVE_AND_DISCARD]: {
            onAction: () => {
                handleClose();
                onDrawingReset?.();
                onExitDrawing?.();
            },
            onClose: () => {
                handleClose();
                setSelectedFeature(undefined);
                onCompleteDrawing?.();
                onExitDrawing?.();
            },
            resetType: RESET_STATE.LEAVE_AND_DISCARD
        },
        [RESET_STATE.ABANDON_AND_CREATE]: {
            onAction: () => {
                handleClose();
            },
            onClose: handleClose,
            resetType: RESET_STATE.ABANDON_AND_CREATE
        }
    };

    const handleDrawingToolChange = (drawingTool: DrawingTool) => {
        if (shouldShowResetPopup) {
            setIsResetPopupOpen(true);
            const resetState = RESET_STATE_MAP[RESET_STATE.DELETE_AND_START_OVER];
            const { onAction } = resetState;

            resetState.onAction = () => {
                onAction();
                setDrawingTool(drawingTool);
            };

            setResetState(resetState);

            return;
        }

        setDrawingTool(drawingTool);
    };

    const handleDrawingModeChange = (drawingMode: DrawingMode) => {
        if (shouldShowResetPopup) {
            setIsResetPopupOpen(true);
            const resetState = RESET_STATE_MAP[RESET_STATE.DELETE_AND_START_OVER];
            const { onAction } = resetState;

            resetState.onAction = () => {
                onAction();
                setDrawingMode(drawingMode);
            };

            setResetState(resetState);

            return;
        }

        setDrawingMode(drawingMode);
    };

    const handlePointModeChange = (pointMode: PointMode) => {
        if (shouldShowResetPopup) {
            setIsResetPopupOpen(true);
            const resetState = RESET_STATE_MAP[RESET_STATE.DELETE_AND_START_OVER];
            const { onAction } = resetState;

            resetState.onAction = () => {
                onAction();
                setPointMode(pointMode);
            };

            setResetState(resetState);

            return;
        }

        setPointMode(pointMode);
    };

    const handleExitDrawing = () => {
        if (shouldShowResetPopup) {
            setIsResetPopupOpen(true);
            setResetState(RESET_STATE_MAP[RESET_STATE.LEAVE_AND_DISCARD]);

            return;
        }

        onExitDrawing?.();
    };

    return {
        handleDrawingModeChange,
        handleDrawingToolChange,
        handleExitDrawing,
        handlePointModeChange,
        isResetPopupOpen,
        resetState,
        setIsResetPopupOpen
    };
};

export default useBoundaryReset;
